import { ElementType, FC, useCallback, useMemo } from "react"
import {
  Box,
  Dialog,
  IconButton,
  IconButtonProps,
  Typography,
  useTheme,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import { getIcon } from "utils/icon"
import { useTranslation } from "providers/i18n"
import { CloseIconButton } from "./CloseIconButton"
import { TabIcon } from "components/icons"

type ParkingLocationButtonProps = IconButtonProps & {
  component?: ElementType
  to?: string
  tooltipOpen: boolean
  onTooltipClose?: () => void
}

export const ParkingLocationButton: FC<ParkingLocationButtonProps> = ({
  sx = {},
  tooltipOpen,
  onTooltipClose = () => {},
  onClick: onClickButton = () => {},
  ...props
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const {
    container,
    button,
    carIcon: carIconStyle,
    tooltip,
    tooltipPrimaryText,
    tooltipSecondaryText,
    tapHereIcon: tapHereIconStyle,
    closeButton,
    activeButton,
    dialog,
  } = theme.components[`ParkingLocationButton`]?.styleOverrides || {}
  const carIcon = useMemo(
    () => getIcon(theme?.assets?.carIcon, carIconStyle),
    [theme, carIconStyle]
  )
  const tapHereIcon = useMemo(
    () =>
      getIcon(theme?.assets?.tapHereIcon, tapHereIconStyle) || (
        <TabIcon sx={tapHereIconStyle} />
      ),
    [theme, tapHereIconStyle]
  )
  const onClickTooltip = useCallback(
    (e) => {
      onClickButton(e)
      onTooltipClose()
    },
    [onClickButton, onTooltipClose]
  )

  return (
    <>
      <Dialog open={tooltipOpen} sx={dialog} onClose={onTooltipClose}>
        <Box sx={tooltip}>
          <Typography component="p" sx={tooltipPrimaryText}>
            {t("Tap Here!")}
          </Typography>
          <Typography component="p" sx={tooltipSecondaryText}>
            {t("to find your car")}
          </Typography>
          {tapHereIcon}
          <CloseIconButton sx={closeButton} onClick={onTooltipClose} />
        </Box>
        <IconButton sx={activeButton} onClick={onClickTooltip}>
          {carIcon}
        </IconButton>
      </Dialog>
      <Box sx={deepmerge(container, sx)}>
        <IconButton sx={button} onClick={onClickButton} {...props}>
          {carIcon}
        </IconButton>
      </Box>
    </>
  )
}
